/**
 * AssignmentsCalendar — Calendario de cobertura por GRUPO (E-R5a).
 *
 * Fuente de verdad: `groups[].members[].schedule` (NO guard_shifts).
 *
 * Vistas:
 *   - Semanal (default): una fila por grupo, 7 celdas día con bloque
 *     de horario consolidado del grupo.
 *   - Diaria: vendedores con su horario individual del día seleccionado.
 *   - Mensual: placeholder (E-R5b/c).
 *
 * Interacciones:
 *   - Click en bloque de día → panel lateral con todos los vendedores
 *     del grupo y botón "Editar horario" por cada uno.
 *   - Editar horario → VendorScheduleModal → onSave → PUT /roulette/groups/{id}
 *
 * NO toca: guard_shifts, guard_schedule, guard_templates, /api/guards/*.
 */
import { useEffect, useMemo, useState, useCallback } from 'react';
import api from '@/lib/api';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { toast } from 'sonner';
import {
  Calendar, ChevronLeft, ChevronRight, RefreshCw, AlertTriangle,
  Users, Pencil, X, Trash2, Bug,
} from 'lucide-react';
import VendorScheduleModal from '@/components/VendorScheduleModal';
import { useAuth } from '@/contexts/AuthContext';
import DebugAssignmentsPanel, { buildAssignmentsDebugJson } from '@/components/DebugAssignmentsPanel';

// (drag-to-create removido en E-R5d → ya no necesitamos HOURS)

// ─── Constantes copiadas de GroupsPage.js (para evitar dependencias circulares) ──

const DAYS = [
  { key: 'monday', label: 'Lunes', short: 'Lun', isWeekday: true },
  { key: 'tuesday', label: 'Martes', short: 'Mar', isWeekday: true },
  { key: 'wednesday', label: 'Miércoles', short: 'Mié', isWeekday: true },
  { key: 'thursday', label: 'Jueves', short: 'Jue', isWeekday: true },
  { key: 'friday', label: 'Viernes', short: 'Vie', isWeekday: true },
  { key: 'saturday', label: 'Sábado', short: 'Sáb', isWeekday: false },
  { key: 'sunday', label: 'Domingo', short: 'Dom', isWeekday: false },
];

const EMPTY_VERTICAL_FILTERS = {
  property_ids: [],
  zones: [],
  car_model_ids: [],
  development_ids: [],
  includes_used: false,
  includes_promotions: false,
};

// Construye members[] a partir de un grupo del backend (legacy fallback).
// Copia 1:1 de GroupsPage.js para no acoplar componentes.
const buildMembersFromGroup = (group) => {
  if (Array.isArray(group?.members) && group.members.length > 0) {
    return group.members.map(m => ({
      vendor_id: m.vendor_id || '',
      schedule: m.schedule && typeof m.schedule === 'object' ? m.schedule : {},
      vertical_filters: { ...EMPTY_VERTICAL_FILTERS, ...(m.vertical_filters || {}) },
    }));
  }
  const legacyVendorIds = group?.vendor_ids || [];
  const legacySchedule = group?.schedule || {};
  return legacyVendorIds.map(vid => ({
    vendor_id: vid,
    schedule: (legacySchedule[vid] && typeof legacySchedule[vid] === 'object') ? legacySchedule[vid] : {},
    vertical_filters: { ...EMPTY_VERTICAL_FILTERS },
  }));
};

// Días lun-vie sin cobertura. [] si nadie ha configurado schedule todavía.
// Copia 1:1 de GroupsPage.js.
const getWeekdayCoverageGaps = (members) => {
  const hasAnyScheduledDay = (members || []).some(m =>
    m.schedule && Object.values(m.schedule).some(d => d && typeof d === 'object'),
  );
  if (!hasAnyScheduledDay) return [];
  return DAYS.filter(d => d.isWeekday).filter(d =>
    !(members || []).some(m => m.schedule?.[d.key]?.active),
  );
};

// Paleta de colores por grupo (asignación por índice).
const GROUP_PALETTE = [
  { bg: '#534AB7', soft: 'rgba(83,74,183,0.35)', text: '#E9E5FF' },
  { bg: '#0F6E56', soft: 'rgba(15,110,86,0.35)', text: '#D9F5EC' },
  { bg: '#BA7517', soft: 'rgba(186,117,23,0.35)', text: '#FFE9CC' },
  { bg: '#993C1D', soft: 'rgba(153,60,29,0.35)', text: '#FFD8CC' },
  { bg: '#185FA5', soft: 'rgba(24,95,165,0.35)', text: '#D8EAFF' },
  { bg: '#3B6D11', soft: 'rgba(59,109,17,0.35)', text: '#DEF5C8' },
  { bg: '#993556', soft: 'rgba(153,53,86,0.35)', text: '#FFD2DF' },
  { bg: '#5F5E5A', soft: 'rgba(95,94,90,0.35)', text: '#E6E5E0' },
];
const colorForGroup = (idx) => GROUP_PALETTE[idx % GROUP_PALETTE.length];

// ─── Helpers de fechas (semana lunes-domingo) ────────────────────────

// Lunes 00:00 de la semana que contiene `date`.
const getWeekStart = (date) => {
  const d = new Date(date);
  const day = d.getDay();
  const diff = d.getDate() - day + (day === 0 ? -6 : 1);
  d.setDate(diff);
  d.setHours(0, 0, 0, 0);
  return d;
};

// Array de 7 Dates (lun-dom) de la semana de `date`, mapeo posicional con DAY_KEYS.
const getWeekDays = (date) => {
  const monday = getWeekStart(date);
  return Array.from({ length: 7 }, (_, i) => {
    const d = new Date(monday);
    d.setDate(monday.getDate() + i);
    return d;
  });
};

// Keys de schedule en MongoDB, en el mismo orden posicional que getWeekDays.
// weekDays[i] (lun-dom) ↔ DAY_KEYS[i] ('monday'…'sunday') ↔ member.schedule[KEY].
const DAY_KEYS = ['monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday', 'sunday'];

// Schedule "default" para los 6 días NO tocados al crear el primer bloque de
// un member que tenía schedule:{}. Mantenemos active=false para no inventar
// disponibilidad que el admin no autorizó explícitamente.
const buildDefaultWeek = () => DAY_KEYS.reduce((acc, k) => ({ ...acc, [k]: { active: false, start_hour: 9, end_hour: 18 } }), {});

const sameYMD = (a, b) => a.getFullYear() === b.getFullYear() && a.getMonth() === b.getMonth() && a.getDate() === b.getDate();

const SHORT_LABELS = ['Lun', 'Mar', 'Mié', 'Jue', 'Vie', 'Sáb', 'Dom'];
const MONTH_LABELS = ['Ene', 'Feb', 'Mar', 'Abr', 'May', 'Jun', 'Jul', 'Ago', 'Sep', 'Oct', 'Nov', 'Dic'];

// "Lun 2 – Dom 8 Jun 2026"  o  "Lun 28 May – Dom 3 Jun 2026"  (cruzando mes).
const fmtWeekTitle = (monday, sunday) => {
  const sameMonth = monday.getMonth() === sunday.getMonth() && monday.getFullYear() === sunday.getFullYear();
  const sameYear = monday.getFullYear() === sunday.getFullYear();
  const left = sameMonth
    ? `Lun ${monday.getDate()}`
    : `Lun ${monday.getDate()} ${MONTH_LABELS[monday.getMonth()]}${sameYear ? '' : ` ${monday.getFullYear()}`}`;
  const right = `Dom ${sunday.getDate()} ${MONTH_LABELS[sunday.getMonth()]} ${sunday.getFullYear()}`;
  return `${left} – ${right}`;
};

const fmtHour = (h) => {
  if (h == null) return '';
  const n = Math.max(0, Math.min(24, parseInt(h, 10) || 0));
  return `${String(n).padStart(2, '0')}:00`;
};

// Rango de cobertura del GRUPO en un día (toma el start más temprano y el end más tardío
// entre los members activos). Detecta si hay heterogeneidad para mostrar el asterisco.
const groupDayCoverage = (members, dayKey) => {
  const actives = (members || [])
    .map(m => m.schedule?.[dayKey])
    .filter(s => s && s.active);
  if (actives.length === 0) return null;
  const starts = actives.map(s => s.start_hour ?? 0);
  const ends = actives.map(s => s.end_hour ?? 0);
  const minStart = Math.min(...starts);
  const maxEnd = Math.max(...ends);
  const heterogeneous = (
    starts.some(s => s !== starts[0]) || ends.some(e => e !== ends[0])
  );
  return { start: minStart, end: maxEnd, count: actives.length, heterogeneous };
};

const summarizeMemberWeek = (schedule) => {
  if (!schedule || typeof schedule !== 'object') return 'Sin horario';
  const active = DAYS.filter(d => schedule?.[d.key]?.active);
  if (active.length === 0) return 'Sin horario';
  // Si todos los días activos tienen el mismo rango, lo agrupamos.
  const first = schedule[active[0].key];
  const allSame = active.every(d => {
    const s = schedule[d.key];
    return s.start_hour === first.start_hour && s.end_hour === first.end_hour;
  });
  if (allSame) {
    return `${active.map(d => d.short).join('·')} ${fmtHour(first.start_hour)}–${fmtHour(first.end_hour)}`;
  }
  // Resumen multi-rango
  return active.map(d => {
    const s = schedule[d.key];
    return `${d.short} ${fmtHour(s.start_hour)}–${fmtHour(s.end_hour)}`;
  }).join(' · ');
};

// ─── Componente principal ────────────────────────────────────────────

export default function AssignmentsCalendar() {
  const { user } = useAuth();
  const [view, setView] = useState('week');               // 'week' | 'day' | 'month'
  const [currentDate, setCurrentDate] = useState(() => new Date());
  const [groups, setGroups] = useState([]);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  // Panel lateral: grupo seleccionado al hacer click en un bloque (vista semanal).
  const [selectedGroup, setSelectedGroup] = useState(null);
  // Vista diaria: día seleccionado (default = hoy o lunes).
  const today = useMemo(() => new Date(), []);
  const initialDayKey = useMemo(() => {
    const wd = today.getDay();
    const idx = wd === 0 ? 6 : wd - 1;
    return DAYS[idx].key;
  }, [today]);
  const [selectedDayKey, setSelectedDayKey] = useState(initialDayKey);

  // Modal de edición de horario por vendedor.
  const [vendorModalOpen, setVendorModalOpen] = useState(false);
  const [vendorEditing, setVendorEditing] = useState(null); // {group, member, vendorName}

  // ── Modal de edición de horario por GRUPO (E-R5d) ─────────────────────
  // Al guardar, aplica el schedule a TODOS los members del grupo.
  const [groupScheduleOpen, setGroupScheduleOpen] = useState(false);
  const [groupScheduleEditing, setGroupScheduleEditing] = useState(null); // {groupId, groupName, dayKey, currentSchedule, members, color}

  // ── Debug panel para diagnóstico de asignaciones ─────────────────────
  const [debugOpen, setDebugOpen] = useState(false);
  const [debugJson, setDebugJson] = useState(null);
  const [debugLoading, setDebugLoading] = useState(false);

  const openDebug = async () => {
    setDebugLoading(true);
    try {
      const [rrRes, leadsRes] = await Promise.all([
        api.get('/leads/round-robin-config').catch(() => ({ data: {} })),
        api.get('/leads').catch(() => ({ data: [] })),
      ]);
      const rrConfig = rrRes.data || {};
      // paginacion-lista-leads: GET /leads ahora devuelve {items,total,skip,limit}.
      const recentLeads = Array.isArray(leadsRes.data) ? leadsRes.data : (leadsRes.data?.items || leadsRes.data?.leads || []);
      setDebugJson(buildAssignmentsDebugJson({ groups, users, user, rrConfig, recentLeads }));
      setDebugOpen(true);
    } finally {
      setDebugLoading(false);
    }
  };

  // ── Carga inicial ──
  const loadAll = useCallback(async () => {
    setLoading(true);
    try {
      const [g, t] = await Promise.all([
        api.get('/roulette/groups'),
        api.get('/team').catch(() => ({ data: [] })),
      ]);
      setGroups(Array.isArray(g.data) ? g.data : []);
      setUsers(Array.isArray(t.data) ? t.data : []);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Error cargando grupos');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { loadAll(); }, [loadAll]);

  // ── Filas derivadas para el render ──
  const calendarRows = useMemo(() => {
    return (groups || []).map((g, idx) => {
      const members = buildMembersFromGroup(g).map(m => ({
        ...m,
        name: users.find(u => u.id === m.vendor_id)?.name || 'Vendedor eliminado',
      }));
      const gaps = getWeekdayCoverageGaps(members);
      return {
        id: g.id,
        name: g.name,
        is_active: g.is_active !== false,
        specialty: g.specialty || {},
        raw: g,
        members,
        coverageGaps: gaps,
        color: colorForGroup(idx),
      };
    });
  }, [groups, users]);

  // ── Días visibles en la cabecera (7 lun-dom de la semana actual) ──
  // weekDays[i] (Date) ↔ DAY_KEYS[i] (schedule key) — mapeo posicional, mismo orden.
  const weekDays = useMemo(() => getWeekDays(currentDate).map((date, i) => ({
    key: DAY_KEYS[i],
    short: SHORT_LABELS[i],
    label: DAYS[i].label,
    isWeekday: DAYS[i].isWeekday,
    date,
    isToday: sameYMD(date, today),
  })), [currentDate, today]);

  const weekTitle = useMemo(
    () => `Semana del ${fmtWeekTitle(weekDays[0].date, weekDays[6].date)}`,
    [weekDays],
  );

  // Navegación de fechas (Date-based, sin strings ISO).
  const goPrev = () => setCurrentDate((d) => {
    const prev = new Date(d);
    prev.setDate(prev.getDate() - (view === 'day' ? 1 : 7));
    return prev;
  });
  const goNext = () => setCurrentDate((d) => {
    const next = new Date(d);
    next.setDate(next.getDate() + (view === 'day' ? 1 : 7));
    return next;
  });
  const goToday = () => setCurrentDate(new Date());

  // ── Guardar schedule de un vendor en el grupo ──
  const saveVendorSchedule = async (newScheduleBackend) => {
    if (!vendorEditing) return;
    const { group, member } = vendorEditing;
    setSaving(true);
    try {
      // Reconstruir members[] del grupo con el schedule actualizado para este vendor.
      const original = buildMembersFromGroup(group.raw);
      const updatedMembers = original.map(m => (
        m.vendor_id === member.vendor_id
          ? { ...m, schedule: newScheduleBackend }
          : m
      ));
      await api.put(`/roulette/groups/${group.id}`, {
        name: group.raw.name,
        is_active: group.raw.is_active !== false,
        max_leads_per_vendor: group.raw.max_leads_per_vendor ?? -1,
        members: updatedMembers,
        specialty: group.raw.specialty || { car_inventory_types: [], product_ids: [] },
      });
      toast.success('Horario actualizado');
      setVendorModalOpen(false);
      setVendorEditing(null);
      await loadAll();
      // Si el panel lateral estaba abierto para este grupo, refrescarlo con la versión más nueva.
      if (selectedGroup?.id === group.id) {
        // El refresco viene por loadAll → calendarRows recalcula y volveremos a abrir el panel actualizado.
        setSelectedGroup(prev => prev ? { ...prev, _refreshTick: Date.now() } : prev);
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'No se pudo guardar el horario');
    } finally {
      setSaving(false);
    }
  };

  // ── Abrir VendorScheduleModal pero configurado para el GRUPO completo ──
  // Al guardar, el schedule se replica a todos los members del grupo. El
  // schedule pre-cargado en el modal es el del primer member que tenga
  // configuración, para que el admin pueda partir del estado actual.
  const openGroupScheduleModal = useCallback((row, dayKey) => {
    const baseMembers = row.members || [];
    if (baseMembers.length === 0) {
      toast.warning('Este grupo no tiene vendedores. Agrégalos desde el tab "Grupos" antes de configurar horarios.');
      return;
    }
    const firstMemberWithSchedule = baseMembers.find(
      m => m.schedule && Object.keys(m.schedule).length > 0,
    );
    const groupSchedule = firstMemberWithSchedule?.schedule || {};
    setGroupScheduleEditing({
      groupId: row.id,
      groupName: row.name,
      dayKey,
      currentSchedule: groupSchedule,
      members: baseMembers,
      color: row.color,
    });
    setGroupScheduleOpen(true);
  }, []);

  // ── Guardar horario del grupo: replica a todos los members ──
  const saveGroupSchedule = useCallback(async (newSchedule) => {
    if (!groupScheduleEditing) return;
    const { groupId, groupName, members } = groupScheduleEditing;
    // Reutilizamos buildMembersFromGroup para incluir vendor_id/vertical_filters/legacy data
    const rawGroup = groups.find(g => g.id === groupId);
    const baseMembers = rawGroup ? buildMembersFromGroup(rawGroup) : members;
    const updatedMembers = baseMembers.map(m => ({
      ...m,
      schedule: newSchedule,
    }));
    setSaving(true);
    try {
      await api.put(`/roulette/groups/${groupId}`, {
        members: updatedMembers,
        vendor_ids: updatedMembers.map(m => m.vendor_id).filter(Boolean),
      });
      toast.success(`Horario actualizado para el grupo ${groupName}`);
      setGroupScheduleOpen(false);
      setGroupScheduleEditing(null);
      await loadAll();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Error al guardar horario del grupo');
    } finally {
      setSaving(false);
    }
  }, [groupScheduleEditing, groups, loadAll]);

  // ── Quitar cobertura: pone active:false en ese dayKey para todos los members ──
  const removeCoverageForDay = useCallback(async (group, dayKey) => {
    if (!group?.raw) return;
    if (!window.confirm(`¿Quitar la cobertura de ${DAYS.find(d => d.key === dayKey)?.label || dayKey} para "${group.name}"?\n\nSe desactivará el día en todos los vendedores del grupo. El resto de horarios se preserva.`)) {
      return;
    }
    const baseMembers = buildMembersFromGroup(group.raw);
    const updated = baseMembers.map(m => {
      const prev = m.schedule?.[dayKey] || { start_hour: 9, end_hour: 18 };
      return {
        ...m,
        schedule: {
          ...(m.schedule || {}),
          [dayKey]: { active: false, start_hour: prev.start_hour ?? 9, end_hour: prev.end_hour ?? 18 },
        },
      };
    });
    setSaving(true);
    try {
      await api.put(`/roulette/groups/${group.id}`, {
        members: updated,
        vendor_ids: updated.map(m => m.vendor_id).filter(Boolean),
      });
      toast.success(`Cobertura de ${DAYS.find(d => d.key === dayKey)?.short || dayKey} removida en ${group.name}`);
      await loadAll();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'No se pudo quitar la cobertura');
    } finally {
      setSaving(false);
    }
  }, [loadAll]);

  // Resync del panel lateral cuando llegan nuevos datos.
  const liveSelectedGroup = useMemo(() => {
    if (!selectedGroup) return null;
    return calendarRows.find(r => r.id === selectedGroup.id) || null;
  }, [selectedGroup, calendarRows]);

  // ─── Render ───
  return (
    <Card className="bg-card/40 border-white/5" data-testid="assignments-calendar">
      <CardContent className="p-5 space-y-4">
        {/* Toolbar */}
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div className="flex items-center gap-2">
            <Calendar className="w-5 h-5 text-cyan-300" />
            <h3 className="text-base font-heading font-semibold text-white">Calendario de Asignaciones</h3>
            <span className="text-[10px] px-2 py-0.5 rounded-full bg-violet-400/10 text-violet-200 border border-violet-400/20">por grupo · members.schedule</span>
          </div>
          <div className="flex items-center gap-2 flex-wrap">
            <div className="flex bg-white/5 rounded-full p-0.5" data-testid="calendar-view-switcher">
              {[
                { key: 'day', label: 'Diaria' },
                { key: 'week', label: 'Semanal' },
                { key: 'month', label: 'Mensual' },
              ].map(v => (
                <button
                  key={v.key}
                  onClick={() => setView(v.key)}
                  className={`px-3 py-1 text-xs rounded-full transition ${view === v.key ? 'bg-cyan-400 text-slate-950 font-bold' : 'text-white/60 hover:text-white/80'}`}
                  data-testid={`calendar-view-${v.key}`}
                >
                  {v.label}
                </button>
              ))}
            </div>
            <Button size="sm" variant="ghost" onClick={loadAll} className="h-8 text-[11px] text-white/70 hover:text-white" data-testid="calendar-reload">
              <RefreshCw className={`w-3.5 h-3.5 mr-1 ${loading ? 'animate-spin' : ''}`} /> Actualizar grupos
            </Button>
            <Button
              size="sm"
              variant="ghost"
              onClick={openDebug}
              disabled={debugLoading}
              className="h-8 text-[11px] text-white/60 hover:text-white gap-1"
              title="Debug Info — para diagnóstico con Codex"
              data-testid="debug-assignments-btn"
            >
              <Bug className="w-3.5 h-3.5" />
              {debugLoading ? 'Cargando…' : 'Debug'}
            </Button>
          </div>
        </div>

        {/* Navegación + título */}
        {view !== 'month' && (
          <div className="flex flex-wrap items-center gap-2 p-3 rounded-lg bg-black/20 border border-white/5">
            <Button size="sm" variant="ghost" onClick={goPrev} className="h-8 w-8 p-0 text-white/70" data-testid="calendar-prev">
              <ChevronLeft className="w-4 h-4" />
            </Button>
            <p className="text-xs text-white font-medium min-w-[14rem]" data-testid="calendar-title">
              {view === 'week' ? weekTitle : (
                weekDays.find(d => d.key === selectedDayKey)?.label + ' ' +
                weekDays.find(d => d.key === selectedDayKey)?.date.getDate()
              )}
            </p>
            <Button size="sm" variant="ghost" onClick={goNext} className="h-8 w-8 p-0 text-white/70" data-testid="calendar-next">
              <ChevronRight className="w-4 h-4" />
            </Button>
            <Button size="sm" variant="ghost" onClick={goToday} className="h-8 text-[11px] text-cyan-300" data-testid="calendar-today">
              Hoy
            </Button>

            {view === 'day' && (
              <div className="flex gap-1 ml-auto">
                {DAYS.map(d => (
                  <button
                    key={d.key}
                    onClick={() => setSelectedDayKey(d.key)}
                    className={`text-[10px] px-2.5 py-1 rounded-full transition ${
                      selectedDayKey === d.key
                        ? 'bg-cyan-400 text-slate-950 font-bold'
                        : 'bg-white/5 text-white/55 hover:text-white/80'
                    }`}
                    data-testid={`calendar-day-chip-${d.key}`}
                  >
                    {d.short}
                  </button>
                ))}
              </div>
            )}
          </div>
        )}

        {/* Loading */}
        {loading && <p className="text-center text-white/55 text-xs py-8">Cargando grupos…</p>}

        {/* Estado vacío */}
        {!loading && calendarRows.length === 0 && (
          <div className="py-10 text-center" data-testid="calendar-empty">
            <Users className="w-10 h-10 mx-auto text-white/15 mb-2" />
            <p className="text-xs text-white/55">No hay grupos creados todavía.</p>
            <p className="text-[10px] text-white/40 mt-1">Crea grupos desde el tab "Grupos" para configurar coberturas.</p>
          </div>
        )}

        {/* Vista semanal */}
        {!loading && view === 'week' && calendarRows.length > 0 && (
          <WeekView
            rows={calendarRows}
            weekDays={weekDays}
            onCellClick={(row) => setSelectedGroup(row)}
            onEmptyCellClick={openGroupScheduleModal}
            saving={saving}
          />
        )}

        {/* Vista diaria */}
        {!loading && view === 'day' && calendarRows.length > 0 && (
          <DayView
            rows={calendarRows}
            dayKey={selectedDayKey}
            onEditVendor={(row, member) => {
              setVendorEditing({
                group: row,
                member,
                vendorName: member.name,
              });
              setVendorModalOpen(true);
            }}
          />
        )}

        {/* Vista mensual — placeholder hasta E-R5b */}
        {!loading && view === 'month' && (
          <div className="py-10 text-center" data-testid="calendar-month-placeholder">
            <Calendar className="w-10 h-10 mx-auto text-white/15 mb-2" />
            <p className="text-xs text-white/55">Vista mensual disponible en próxima iteración.</p>
            <p className="text-[10px] text-white/40 mt-1">Por ahora, usa las vistas Semanal y Diaria.</p>
          </div>
        )}
      </CardContent>

      {/* Panel lateral: grupo seleccionado (vista semanal) */}
      <Dialog open={!!liveSelectedGroup} onOpenChange={(o) => !o && setSelectedGroup(null)}>
        <DialogContent className="bg-[#0f1117] border-white/10 text-white max-w-md" data-testid="group-vendor-panel">
          {liveSelectedGroup && (
            <>
              <DialogHeader>
                <div className="flex items-center gap-2">
                  <span
                    className="w-3 h-3 rounded-full shrink-0"
                    style={{ background: liveSelectedGroup.color.bg }}
                  />
                  <DialogTitle className="text-sm">{liveSelectedGroup.name}</DialogTitle>
                </div>
                <DialogDescription className="text-[11px] text-white/55">
                  {liveSelectedGroup.members.length} vendedor(es) · Horario individual por vendedor
                </DialogDescription>
              </DialogHeader>

              <div className="space-y-2 max-h-[60vh] overflow-y-auto pr-1">
                {liveSelectedGroup.members.length === 0 && (
                  <p className="text-xs text-white/50 text-center py-6">Este grupo no tiene vendedores.</p>
                )}
                {liveSelectedGroup.members.map(m => (
                  <div
                    key={m.vendor_id}
                    className="flex items-start justify-between gap-2 p-3 rounded-lg bg-white/[0.03] border border-white/5"
                    data-testid={`panel-member-${m.vendor_id}`}
                  >
                    <div className="flex items-start gap-2 min-w-0">
                      <div className="w-8 h-8 rounded-full bg-violet-500/20 text-violet-200 text-[11px] font-bold flex items-center justify-center shrink-0">
                        {(m.name || '?').split(' ').map(p => p[0]).slice(0, 2).join('').toUpperCase()}
                      </div>
                      <div className="min-w-0">
                        <p className="text-xs text-white font-medium truncate">{m.name}</p>
                        <p className="text-[10px] text-white/55 mt-0.5">{summarizeMemberWeek(m.schedule)}</p>
                      </div>
                    </div>
                    <Button
                      size="sm"
                      variant="ghost"
                      onClick={() => {
                        setVendorEditing({
                          group: liveSelectedGroup,
                          member: m,
                          vendorName: m.name,
                        });
                        setVendorModalOpen(true);
                      }}
                      className="h-7 text-[10px] text-cyan-300 hover:text-cyan-200 hover:bg-cyan-500/10 shrink-0"
                      data-testid={`panel-edit-${m.vendor_id}`}
                    >
                      <Pencil className="w-3 h-3 mr-1" /> Editar horario
                    </Button>
                  </div>
                ))}
              </div>

              {/* Quitar cobertura por día (E-R5b) */}
              {(() => {
                const coveredDays = DAYS.filter(d => groupDayCoverage(liveSelectedGroup.members, d.key));
                if (coveredDays.length === 0) return null;
                return (
                  <div className="pt-3 border-t border-white/5" data-testid="panel-remove-coverage-section">
                    <p className="text-[10px] uppercase tracking-widest text-white/45 mb-2">Quitar cobertura</p>
                    <div className="flex flex-wrap gap-1.5">
                      {coveredDays.map(d => (
                        <Button
                          key={d.key}
                          size="sm"
                          variant="outline"
                          disabled={saving}
                          onClick={() => removeCoverageForDay(liveSelectedGroup, d.key)}
                          className="h-7 text-[10px] border-rose-400/30 text-rose-300 hover:bg-rose-500/10"
                          data-testid={`panel-remove-coverage-${d.key}`}
                        >
                          <Trash2 className="w-3 h-3 mr-1" /> {d.short}
                        </Button>
                      ))}
                    </div>
                    <p className="text-[9px] text-white/40 mt-1.5 leading-snug">
                      Desactiva el día seleccionado en todos los vendedores del grupo. El resto de horarios y los de otros días se preservan.
                    </p>
                  </div>
                );
              })()}

              <div className="flex justify-between items-center pt-2 border-t border-white/5">
                <Button
                  size="sm"
                  variant="outline"
                  disabled={saving}
                  onClick={() => {
                    // Abrir modal del grupo pre-cargado con el primer día con cobertura
                    // (o lunes si no hay ninguno).
                    const firstCovered = DAYS.find(d => groupDayCoverage(liveSelectedGroup.members, d.key));
                    openGroupScheduleModal(liveSelectedGroup, firstCovered?.key || 'monday');
                    setSelectedGroup(null);
                  }}
                  className="h-7 text-[10px] border-cyan-400/30 text-cyan-300 hover:bg-cyan-500/10"
                  data-testid="panel-edit-group-schedule"
                >
                  <Pencil className="w-3 h-3 mr-1" /> Editar horario del grupo
                </Button>
                <Button variant="ghost" size="sm" onClick={() => setSelectedGroup(null)} className="text-xs" data-testid="panel-close">
                  <X className="w-3 h-3 mr-1" /> Cerrar
                </Button>
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>

      {/* Modal de edición de horario por vendedor (panel individual) */}
      <VendorScheduleModal
        open={vendorModalOpen}
        onOpenChange={(o) => {
          setVendorModalOpen(o);
          if (!o) setVendorEditing(null);
        }}
        vendorName={vendorEditing?.vendorName || ''}
        schedule={vendorEditing?.member?.schedule || {}}
        onSave={saveVendorSchedule}
        saving={saving}
      />

      {/* Modal de edición de horario por GRUPO (E-R5d) — aplica a todos los members */}
      <VendorScheduleModal
        open={groupScheduleOpen}
        onOpenChange={(o) => {
          setGroupScheduleOpen(o);
          if (!o) setGroupScheduleEditing(null);
        }}
        vendorName={groupScheduleEditing ? `Grupo ${groupScheduleEditing.groupName}` : ''}
        schedule={groupScheduleEditing?.currentSchedule || {}}
        onSave={saveGroupSchedule}
        saving={saving}
      />

      {/* Debug panel para diagnóstico (Codex+MCP) */}
      <DebugAssignmentsPanel
        open={debugOpen}
        onClose={() => setDebugOpen(false)}
        debugJson={debugJson}
      />
    </Card>
  );
}

// ─── Sub-componentes ─────────────────────────────────────────────────

function WeekView({ rows, weekDays, onCellClick, onEmptyCellClick, saving }) {
  return (
    <div className="overflow-x-auto" data-testid="calendar-week-view">
      <div className="min-w-[920px]">
        {/* Header: 1 columna ancha (grupo) + 7 días */}
        <div className="grid grid-cols-[220px_repeat(7,1fr)] gap-px bg-white/5 rounded-t-lg overflow-hidden">
          <div className="bg-slate-900 p-2 text-[11px] text-white/55 uppercase tracking-wider font-semibold">Grupos</div>
          {weekDays.map(d => (
            <div
              key={d.key}
              className={`bg-slate-900 p-2 text-center ${d.isToday ? 'ring-1 ring-inset ring-cyan-400/50' : ''}`}
            >
              <div className={`text-[11px] font-bold ${d.isToday ? 'text-cyan-300' : 'text-white/80'}`}>{d.short}</div>
              <div className={`text-[10px] ${d.isToday ? 'text-cyan-300/80' : 'text-white/40'}`}>{d.date.getDate()}</div>
            </div>
          ))}
        </div>

        {/* Filas por grupo */}
        <div className="grid grid-cols-[220px_repeat(7,1fr)] gap-px bg-white/5 rounded-b-lg overflow-hidden">
          {rows.map(row => (
            <GroupRow
              key={row.id}
              row={row}
              weekDays={weekDays}
              onCellClick={onCellClick}
              onEmptyCellClick={onEmptyCellClick}
              saving={saving}
            />
          ))}
        </div>

        {/* Hint UX */}
        <p className="text-[10px] text-white/35 italic mt-2 pl-1" data-testid="calendar-hint">
          Tip: click sobre una celda vacía para configurar el horario del grupo en ese día. Click sobre un bloque existente para ver y editar a sus vendedores.
        </p>
      </div>
    </div>
  );
}

function GroupRow({ row, weekDays, onCellClick, onEmptyCellClick, saving }) {
  const { color, members, coverageGaps, specialty } = row;
  const specialtyChip = (() => {
    const carTypes = specialty?.car_inventory_types || [];
    const productIds = specialty?.product_ids || [];
    if (carTypes.length > 0) return carTypes.length === 1 ? carTypes[0] : `${carTypes.length} tipos`;
    if (productIds.length > 0) return `${productIds.length} producto(s)`;
    return null;
  })();

  return (
    <>
      {/* Columna izquierda: nombre del grupo */}
      <div
        className="bg-slate-950/80 p-3 flex flex-col gap-1 justify-center border-l-4"
        style={{ borderLeftColor: color.bg }}
        data-testid={`group-row-${row.id}`}
      >
        <div className="flex items-center gap-2 min-w-0">
          <p className="text-[12px] text-white font-semibold truncate" title={row.name}>{row.name}</p>
          {!row.is_active && <span className="text-[9px] px-1.5 py-0.5 rounded bg-white/10 text-white/50">inactivo</span>}
        </div>
        <div className="flex items-center gap-1.5 flex-wrap">
          <span className="text-[10px] text-white/45">{members.length} vendedor{members.length === 1 ? '' : 'es'}</span>
          {specialtyChip && (
            <span className="text-[9px] px-1.5 py-0.5 rounded bg-violet-400/10 text-violet-200 border border-violet-400/15">
              {specialtyChip}
            </span>
          )}
        </div>
        {coverageGaps.length > 0 && (
          <div
            className="flex items-center gap-1 text-[9px] text-amber-200 mt-0.5"
            data-testid={`group-coverage-warning-${row.id}`}
            title={`Sin cobertura: ${coverageGaps.map(d => d.short).join(', ')}`}
          >
            <AlertTriangle className="w-3 h-3" />
            <span>{coverageGaps.length} día{coverageGaps.length === 1 ? '' : 's'} sin cobertura</span>
          </div>
        )}
      </div>

      {/* 7 celdas día */}
      {weekDays.map(d => {
        const coverage = groupDayCoverage(members, d.key);
        return (
          <DayCell
            key={d.key}
            row={row}
            day={d}
            coverage={coverage}
            onClick={() => coverage && onCellClick(row)}
            onEmptyClick={() => !saving && onEmptyCellClick && onEmptyCellClick(row, d.key)}
            saving={saving}
          />
        );
      })}
    </>
  );
}

function DayCell({ row, day, coverage, onClick, onEmptyClick, saving }) {
  const { color } = row;

  // ── Celda con bloque existente: click para abrir panel ───────────────
  if (coverage) {
    return (
      <div className="p-1.5 h-[72px]" data-testid={`day-cell-wrap-${row.id}-${day.key}`}>
        <button
          onClick={onClick}
          className="w-full rounded-md px-2 py-2 text-left transition hover:brightness-125 focus:outline-none focus:ring-2 focus:ring-cyan-400/40 cursor-pointer"
          style={{
            background: color.soft,
            color: color.text,
            border: `1.5px solid ${color.bg}`,
            height: 'calc(100% - 0px)',
            minHeight: '60px',
          }}
          title={coverage.heterogeneous ? 'Horarios individualizados — click para ver detalle' : 'Click para ver vendedores'}
          data-testid={`day-cell-${row.id}-${day.key}`}
        >
          <div className="text-[13px] font-bold leading-tight">
            {fmtHour(coverage.start)} – {fmtHour(coverage.end)}{coverage.heterogeneous && '*'}
          </div>
          <div className="text-[10px] opacity-90 mt-0.5">
            {coverage.count} vendedor{coverage.count === 1 ? '' : 'es'}
          </div>
        </button>
      </div>
    );
  }

  // ── Celda VACÍA: click abre el modal del grupo (E-R5d) ────────────────
  const noMembers = row.members.length === 0;
  return (
    <div className="p-1 h-[72px]" data-testid={`day-cell-wrap-${row.id}-${day.key}`}>
      <button
        onClick={onEmptyClick}
        disabled={saving || noMembers}
        className={`w-full h-full flex items-center justify-center rounded-md transition focus:outline-none focus:ring-2 focus:ring-cyan-400/40 ${
          saving || noMembers ? 'cursor-not-allowed opacity-60' : 'cursor-pointer'
        } ${
          day.isWeekday
            ? 'bg-rose-950/15 border border-rose-400/10 hover:bg-rose-950/30 hover:border-rose-400/30'
            : 'bg-slate-950/20 hover:bg-slate-950/40'
        }`}
        title={noMembers
          ? 'Agrega vendedores al grupo antes de configurar horarios'
          : 'Click para configurar horario de este grupo'}
        data-testid={`day-cell-empty-${row.id}-${day.key}`}
      >
        <span className={`text-[10px] italic ${day.isWeekday ? 'text-rose-300/45' : 'text-white/25'}`}>
          {day.isWeekday ? 'Sin cobertura' : '—'}
        </span>
      </button>
    </div>
  );
}

function DayView({ rows, dayKey, onEditVendor }) {
  return (
    <div className="space-y-3" data-testid="calendar-day-view">
      {rows.map(row => {
        const dayMembers = row.members.map(m => ({
          ...m,
          slot: m.schedule?.[dayKey]?.active ? m.schedule[dayKey] : null,
        }));
        return (
          <div
            key={row.id}
            className="rounded-lg border border-white/5 overflow-hidden"
            data-testid={`day-group-${row.id}`}
          >
            <div
              className="flex items-center gap-2 px-3 py-2 border-l-4"
              style={{ borderLeftColor: row.color.bg, background: 'rgba(15,17,23,0.6)' }}
            >
              <span className="w-2 h-2 rounded-full shrink-0" style={{ background: row.color.bg }} />
              <p className="text-[12px] text-white font-semibold">{row.name}</p>
              <span className="text-[10px] text-white/40">· {row.members.length} vendedor(es)</span>
            </div>
            <div className="divide-y divide-white/5">
              {dayMembers.length === 0 && (
                <p className="text-[11px] text-white/40 px-3 py-3 italic">Grupo sin vendedores.</p>
              )}
              {dayMembers.map(m => (
                <button
                  key={m.vendor_id}
                  onClick={() => onEditVendor(row, m)}
                  className="w-full flex items-center justify-between px-3 py-2 hover:bg-white/[0.03] transition text-left"
                  data-testid={`day-vendor-${row.id}-${m.vendor_id}`}
                >
                  <div className="flex items-center gap-2 min-w-0">
                    <div className="w-7 h-7 rounded-full bg-violet-500/20 text-violet-200 text-[10px] font-bold flex items-center justify-center shrink-0">
                      {(m.name || '?').split(' ').map(p => p[0]).slice(0, 2).join('').toUpperCase()}
                    </div>
                    <p className="text-[11px] text-white truncate">{m.name}</p>
                  </div>
                  <p className="text-[10px] shrink-0">
                    {m.slot ? (
                      <span className="text-emerald-300">{fmtHour(m.slot.start_hour)} – {fmtHour(m.slot.end_hour)}</span>
                    ) : (
                      <span className="text-white/40 italic">Sin turno</span>
                    )}
                  </p>
                </button>
              ))}
            </div>
          </div>
        );
      })}
    </div>
  );
}
